/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `monitoring_info` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class MonitoringInfoMesg : Mesg {
    public constructor() : super("MonitoringInfo", Profile.MesgNum.MONITORING_INFO)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** local_timestamp, in s. Use to convert activity timestamps to local time if device does not support time zone and daylight savings time correction. */
    public var localTimestamp: Instant?
        get() = (getFieldValue(LOCAL_TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(LOCAL_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** Number of values stored in activity_type. */
    public val numActivityType: Int
        get() = getField(ACTIVITY_TYPE_FIELD_NUM)?.numValues ?: 0

    /** activity_type. */
    public var activityType: List<ActivityType?>?
        get() {
            val field = getField(ACTIVITY_TYPE_FIELD_NUM) ?: return null
            return List(field.numValues) { getActivityType(it) }
        }
        set(value) {
            removeField(ACTIVITY_TYPE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setActivityType(index, element) }
        }

    public fun getActivityType(index: Int): ActivityType? =
        (getFieldValue(ACTIVITY_TYPE_FIELD_NUM, index) as? UByte)?.let { ActivityType.fromValue(it) }

    public fun setActivityType(index: Int, value: ActivityType?) {
        setFieldValue(ACTIVITY_TYPE_FIELD_NUM, value?.value, index)
    }

    /** Number of values stored in cycles_to_distance. */
    public val numCyclesToDistance: Int
        get() = getField(CYCLES_TO_DISTANCE_FIELD_NUM)?.numValues ?: 0

    /** cycles_to_distance, in m/cycle. Indexed by activity_type */
    public var cyclesToDistance: List<Double?>?
        get() {
            val field = getField(CYCLES_TO_DISTANCE_FIELD_NUM) ?: return null
            return List(field.numValues) { getCyclesToDistance(it) }
        }
        set(value) {
            removeField(CYCLES_TO_DISTANCE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCyclesToDistance(index, element) }
        }

    public fun getCyclesToDistance(index: Int): Double? =
        getFieldValue(CYCLES_TO_DISTANCE_FIELD_NUM, index) as? Double

    public fun setCyclesToDistance(index: Int, value: Double?) {
        setFieldValue(CYCLES_TO_DISTANCE_FIELD_NUM, value, index)
    }

    /** Number of values stored in cycles_to_calories. */
    public val numCyclesToCalories: Int
        get() = getField(CYCLES_TO_CALORIES_FIELD_NUM)?.numValues ?: 0

    /** cycles_to_calories, in kcal/cycle. Indexed by activity_type */
    public var cyclesToCalories: List<Double?>?
        get() {
            val field = getField(CYCLES_TO_CALORIES_FIELD_NUM) ?: return null
            return List(field.numValues) { getCyclesToCalories(it) }
        }
        set(value) {
            removeField(CYCLES_TO_CALORIES_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCyclesToCalories(index, element) }
        }

    public fun getCyclesToCalories(index: Int): Double? =
        getFieldValue(CYCLES_TO_CALORIES_FIELD_NUM, index) as? Double

    public fun setCyclesToCalories(index: Int, value: Double?) {
        setFieldValue(CYCLES_TO_CALORIES_FIELD_NUM, value, index)
    }

    /** resting_metabolic_rate, in kcal / day. */
    public var restingMetabolicRate: UShort?
        get() = getFieldValue(RESTING_METABOLIC_RATE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(RESTING_METABOLIC_RATE_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val LOCAL_TIMESTAMP_FIELD_NUM: UByte = 0u
        public const val ACTIVITY_TYPE_FIELD_NUM: UByte = 1u
        public const val CYCLES_TO_DISTANCE_FIELD_NUM: UByte = 3u
        public const val CYCLES_TO_CALORIES_FIELD_NUM: UByte = 4u
        public const val RESTING_METABOLIC_RATE_FIELD_NUM: UByte = 5u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("LocalTimestamp", 0u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[1] = Field("ActivityType", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[3] = Field("CyclesToDistance", 3u, BaseType.UINT16, 5000.0, 0.0, "m/cycle", false)
            fields[4] = Field("CyclesToCalories", 4u, BaseType.UINT16, 5000.0, 0.0, "kcal/cycle", false)
            fields[5] = Field("RestingMetabolicRate", 5u, BaseType.UINT16, 1.0, 0.0, "kcal / day", false)
            return fields
        }
    }
}
